import React, { useState, useEffect } from 'react';
import { X, QrCode, CheckCircle2, Copy, Check, ShieldCheck, Globe, ExternalLink, Sparkles } from 'lucide-react';
import { PaymentGateway, PaymentDetails, CustomCharge } from '../types';
import { generatePixData, getEffectiveCurrencyRates } from '../lib/shippingAndPayment';
import { getSavedPaymentGatewayConfigs } from './PaymentMethodsModal';

interface CustomChargePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  charge: CustomCharge;
  onPaymentComplete: (chargeId: string, paymentDetails: PaymentDetails) => Promise<void> | void;
}

export const CustomChargePaymentModal: React.FC<CustomChargePaymentModalProps> = ({
  isOpen,
  onClose,
  charge,
  onPaymentComplete
}) => {
  const [selectedGateway, setSelectedGateway] = useState<PaymentGateway>('Wise');
  const [copiedPix, setCopiedPix] = useState(false);
  const [copiedWise, setCopiedWise] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [savedGatewayConfigs, setSavedGatewayConfigs] = useState<any>({});

  useEffect(() => {
    if (isOpen) {
      setSelectedGateway('Wise');
      setSavedGatewayConfigs(getSavedPaymentGatewayConfigs());
    }
  }, [isOpen]);

  const rates = getEffectiveCurrencyRates();
  const amountBrl = charge.amountJpy * rates.jpyToBrl;

  // Pix Logic
  const defaultPixData = generatePixData(charge.id, amountBrl, "Egami Corp");
  const pixData = {
    ...defaultPixData,
    pixCopiaECola: savedGatewayConfigs.PIX?.pixCopiaECola || defaultPixData.pixCopiaECola
  };

  const handleCopyPix = () => {
    if (pixData.pixCopiaECola) {
      navigator.clipboard.writeText(pixData.pixCopiaECola);
      setCopiedPix(true);
      setTimeout(() => setCopiedPix(false), 2000);
    }
  };

  const handleCopyWise = () => {
    const email = savedGatewayConfigs.Wise?.wiseEmail || 'egami.corporation@gmail.com';
    navigator.clipboard.writeText(email);
    setCopiedWise(true);
    setTimeout(() => setCopiedWise(false), 2000);
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    try {
      await onPaymentComplete(charge.id, {
        method: selectedGateway,
        status: 'Aprovado',
        amountUsd: charge.amountJpy * (rates.jpyToUsd || 0.0065),
        amountJpy: charge.amountJpy,
        baseAmountBrl: amountBrl,
        internationalFeeBrl: 0,
        totalAmountBrl: amountBrl,
        amountTotalBrl: amountBrl,
        transactionId: 'simulated-' + Date.now(),
        paidAt: new Date().toISOString()
      });
      setPaid(true);
      setTimeout(() => {
        onClose();
        setPaid(false);
      }, 2000);
    } catch (error) {
      console.error(error);
      alert('Erro ao confirmar pagamento');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (paid) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-emerald-600 animate-in fade-in duration-300">
        <div className="text-center text-white p-8">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce-short shadow-2xl">
            <CheckCircle2 className="w-12 h-12 text-emerald-600" />
          </div>
          <h2 className="text-3xl font-black mb-2 tracking-tight">Pagamento Confirmado!</h2>
          <p className="text-emerald-100 font-medium">Ordem de pagamento quitada com sucesso.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full sm:max-w-xl min-h-screen sm:min-h-0 sm:rounded-3xl shadow-2xl flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 p-6 pt-10 sm:pt-6 pb-8 relative shrink-0">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white/80 hover:text-white transition-colors cursor-pointer">
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex flex-col items-center text-center mt-2">
            <span className="px-3 py-1 bg-amber-500 text-amber-950 text-[10px] font-black uppercase tracking-widest rounded-full mb-3 shadow-sm border border-amber-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-900" />
              Pagamento Avulso
            </span>
            <h2 className="text-2xl font-black text-white mb-2">{charge.title}</h2>
            {charge.description && (
              <p className="text-xs text-slate-300 max-w-md mb-2">{charge.description}</p>
            )}
            <div className="text-5xl font-black text-white tracking-tighter tabular-nums drop-shadow-md">
              <span className="text-2xl text-emerald-400 font-bold mr-1 align-top">R$</span>
              {amountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-sm font-medium text-slate-400 mt-2">
              Equivalente a ¥ {charge.amountJpy.toLocaleString('ja-JP')}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50 flex flex-col">
          {/* Seletor de Métodos: Apenas Wise (padrão) e PIX */}
          <div className="mb-5 grid grid-cols-2 gap-3">
            <button
              onClick={() => setSelectedGateway('Wise')}
              className={`p-3.5 border rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer relative ${
                selectedGateway === 'Wise'
                  ? 'bg-[#163300] border-[#163300] text-[#9FE870] shadow-md ring-2 ring-[#9FE870]'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <Globe className={`w-5 h-5 ${selectedGateway === 'Wise' ? 'text-[#9FE870]' : 'text-[#163300]'}`} />
                <span className="font-extrabold text-sm">Wise</span>
              </div>
              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${selectedGateway === 'Wise' ? 'bg-[#9FE870]/25 text-[#9FE870]' : 'bg-emerald-100 text-emerald-800'}`}>
                Padrão / Recomendado
              </span>
            </button>

            <button
              onClick={() => setSelectedGateway('PIX')}
              className={`p-3.5 border rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer ${
                selectedGateway === 'PIX'
                  ? 'bg-emerald-600 border-emerald-600 text-white shadow-md ring-2 ring-emerald-400'
                  : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-1.5">
                <QrCode className={`w-5 h-5 ${selectedGateway === 'PIX' ? 'text-white' : 'text-emerald-600'}`} />
                <span className="font-extrabold text-sm">PIX</span>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${selectedGateway === 'PIX' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                Instantâneo (Brasil)
              </span>
            </button>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm grow flex flex-col">
            {selectedGateway === 'Wise' && (
              <div className="space-y-4">
                <div className="p-3 bg-[#163300] text-[#9FE870] rounded-xl flex items-center justify-between gap-2">
                  <span className="text-xs font-bold">Não tem conta na Wise?</span>
                  <a
                    href={savedGatewayConfigs.Wise?.wiseInviteUrl || 'https://wise.com/invite/ilpc/welingtone'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 bg-[#9FE870] hover:bg-[#8edb5f] text-[#163300] font-black text-xs rounded-lg flex items-center gap-1 shrink-0 transition-colors"
                  >
                    <span>Crie sua conta na Wise</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
                  <span className="text-xs text-slate-500 font-bold uppercase block">Conta / E-mail de Destino Wise:</span>
                  <div className="flex items-center justify-between gap-2 bg-white p-2.5 rounded-lg border border-slate-200">
                    <span className="text-sm font-mono font-bold text-slate-900 truncate">
                      {savedGatewayConfigs.Wise?.wiseEmail || 'egami.corporation@gmail.com'}
                    </span>
                    <button onClick={handleCopyWise} className="px-3 py-1.5 bg-[#163300] hover:bg-[#204900] text-[#9FE870] rounded-lg font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors shrink-0">
                      {copiedWise ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      <span>{copiedWise ? 'Copiado!' : 'Copiar'}</span>
                    </button>
                  </div>
                  <div className="flex flex-col gap-1 text-xs text-slate-600">
                    <p>
                      Titular: <strong>{savedGatewayConfigs.Wise?.wiseAccountName || 'Egami Corporation'}</strong>
                    </p>
                    <p className="text-slate-500 text-[11px]">
                      Você pode transferir tanto em <strong>JPY (Ienes)</strong> quanto em <strong>BRL (Reais)</strong> pelo app ou site da Wise.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedGateway === 'PIX' && (
              <div className="text-center space-y-4">
                <div className="p-3 bg-slate-50 rounded-xl inline-block border border-slate-200">
                  <img
                    src={savedGatewayConfigs.PIX?.pixQrCodeImage || `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(pixData.pixCopiaECola)}`}
                    alt="QR Code PIX"
                    className="w-48 h-48 mx-auto mix-blend-multiply"
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-800 mb-2">Pix Copia e Cola</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={pixData.pixCopiaECola}
                      className="w-full text-xs font-mono p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-500 outline-none"
                    />
                    <button onClick={handleCopyPix} className="p-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl transition-colors cursor-pointer shrink-0">
                      {copiedPix ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6 pt-4 border-t border-slate-200">
             <button
              onClick={handleConfirmPayment}
              disabled={loading}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-lg shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="w-5 h-5" />
                  <span>Confirmar Pagamento ({selectedGateway})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

